import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  XCircle,
  MapPin,
  Clock,
  CreditCard,
  Package
} from 'lucide-react'
import { formatPrice } from '../../lib/square'

interface CancelledOrderLayoutProps {
  order: any
}

const CancelledOrderLayout: React.FC<CancelledOrderLayoutProps> = ({ order }) => {
  const navigate = useNavigate()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/orders')}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Orders</span>
          </button>

          <div className="text-right">
            <h1 className="text-2xl font-bold text-foreground">Order #{order.order_number}</h1>
            <p className="text-muted-foreground text-sm">
              Placed on {formatDate(order.created_at)}
            </p>
          </div>
        </div>

        {/* Cancellation Status Banner */}
        <div className="bg-card border-2 border-red-500 rounded-lg p-6 mb-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-foreground mb-2">Order Cancelled</h2>
              <p className="text-muted-foreground mb-4">
                This order was cancelled on {formatDate(order.updated_at || order.created_at)}.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Need help? Contact Support →
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Summary */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center space-x-2">
                <Package size={20} />
                <span>Order Summary</span>
              </h3>

              <div className="space-y-3">
                {order.secret_order_items.map((item: any) => (
                  <div key={item.id} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <div className="flex-1">
                      <span className="font-medium text-foreground">{item.secret_menu_items.name}</span>
                      {item.quantity > 1 && (
                        <span className="text-muted-foreground text-sm ml-2">× {item.quantity}</span>
                      )}
                    </div>
                    <div className="text-foreground font-medium">
                      {formatPrice(item.total_price * 100)}
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-center pt-4 mt-4 border-t-2 border-border">
                  <span className="text-lg font-bold text-foreground">Total</span>
                  <span className="text-xl font-bold text-red-600 dark:text-red-400">
                    {formatPrice(order.total_amount * 100)}
                  </span>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4">Event Details</h3>

              <div className="space-y-3">
                <div>
                  <Link
                    to={`/events/${order.secret_events.id}`}
                    className="font-bold text-foreground hover:text-secret-primary text-lg mb-1 inline-block transition-colors"
                  >
                    {order.secret_events.title}
                  </Link>
                </div>

                <div className="flex items-start space-x-3 text-muted-foreground">
                  <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{order.secret_events.location_name}</p>
                    <p className="text-sm">{order.secret_events.address}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 text-muted-foreground">
                  <Clock size={18} className="flex-shrink-0" />
                  <span>
                    {formatDate(order.secret_events.event_date)} at {formatTime(order.secret_events.event_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Info */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="font-bold text-foreground mb-4 flex items-center space-x-2">
                <CreditCard size={20} />
                <span>Payment</span>
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    {order.square_payment_id ? 'Refunded' : 'Not Charged'}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method:</span>
                  <span className="text-foreground">Square</span>
                </div>

                {order.square_payment_id && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment ID:</span>
                    <span className="text-foreground text-xs">
                      {order.square_payment_id.substring(0, 12)}...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Support */}
            <div className="bg-card rounded-lg p-6 text-center border border-border">
              <h3 className="font-bold text-foreground mb-2">Need Help?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Questions about this cancellation?
              </p>
              <button className="w-full px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-medium">
                Contact Support
              </button>
            </div>

            {/* Action */}
            <div className="bg-card rounded-lg p-6 text-center border border-border">
              <p className="text-muted-foreground text-sm mb-4">
                Ready to try again?
              </p>
              <Link
                to="/events"
                className="w-full inline-block text-center px-4 py-2 bg-secret-primary hover:bg-secret-primary-dark text-white! rounded-lg transition-colors text-sm font-medium"
              >
                Browse Events
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CancelledOrderLayout
